import React from 'react';

/**
 * Four rows of toggle chips for filtering the scatter plot and heatmap.
 * Tickers, Expirations, Moneyness, Scenario columns.
 * All chips ON by default. Toggling filters client-side (no API call).
 */

const EXP_ORDER = ['7d', '30d', '60d', '90d', '180d', '270d', '1y', '1.5y'];
const MONEYNESS_ORDER = [
  '100% OTM', '50% OTM', '35% OTM', '20% OTM', '10% OTM', '5% OTM',
  'ATM',
  '5% ITM', '10% ITM', '20% ITM', '35% ITM', '50% ITM',
];
const SCENARIO_ORDER = [
  '-50', '-40', '-30', '-20', '-15', '-10', '-5', '0',
  '5', '10', '15', '20', '25', '30', '40', '50', '75', '100',
];

function FilterRow({ label, items, activeItems, setActiveItems, formatLabel }) {
  const toggleItem = (item) => {
    setActiveItems(prev => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const allActive = items.every(item => activeItems.has(item));

  const toggleAll = () => {
    if (allActive) {
      setActiveItems(new Set());
    } else {
      setActiveItems(new Set(items));
    }
  };

  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-gray-500 w-20 flex-shrink-0 pt-1">{label}</span>
      <div className="flex flex-wrap gap-1">
        <button
          onClick={toggleAll}
          className={`px-2 py-0.5 text-xs rounded transition-colors ${
            allActive
              ? 'bg-blue-600 text-white'
              : 'bg-gray-600 text-gray-400 hover:bg-gray-500'
          }`}
        >
          All
        </button>
        {items.map(item => (
          <button
            key={item}
            onClick={() => toggleItem(item)}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              activeItems.has(item)
                ? 'bg-gray-600 text-white'
                : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
            }`}
          >
            {formatLabel ? formatLabel(item) : item}
          </button>
        ))}
      </div>
    </div>
  );
}

function ExplorerFilters({
  contracts,
  activeTickers, setActiveTickers,
  activeExpBuckets, setActiveExpBuckets,
  activeMoneynessLabels, setActiveMoneynessLabels,
  activeScenarios, setActiveScenarios,
}) {
  // Extract unique values from data, maintaining display order
  const tickers = [...new Set(contracts.map(c => c.ticker))].sort();
  const expBuckets = EXP_ORDER.filter(e =>
    contracts.some(c => c.exp_bucket === e)
  );
  const moneynessLabels = MONEYNESS_ORDER.filter(m =>
    contracts.some(c => c.moneyness_bucket === m)
  );

  const formatScenario = (s) => {
    const n = Number(s);
    return n > 0 ? `+${n}%` : `${n}%`;
  };

  return (
    <div className="bg-gray-800/50 rounded-lg p-3 space-y-2">
      <FilterRow
        label="Tickers"
        items={tickers}
        activeItems={activeTickers}
        setActiveItems={setActiveTickers}
      />
      <FilterRow
        label="Expirations"
        items={expBuckets}
        activeItems={activeExpBuckets}
        setActiveItems={setActiveExpBuckets}
      />
      <FilterRow
        label="Moneyness"
        items={moneynessLabels}
        activeItems={activeMoneynessLabels}
        setActiveItems={setActiveMoneynessLabels}
      />
      <FilterRow
        label="Scenarios"
        items={SCENARIO_ORDER}
        activeItems={activeScenarios}
        setActiveItems={setActiveScenarios}
        formatLabel={formatScenario}
      />
    </div>
  );
}

export default ExplorerFilters;
